<template>
  <div class="__XYChart">
    <div id="hero-chart-div" ref="chart-div" />

    <div id="hero-chart-legend-div" />

    <!-- <CLoading :loading="loading" /> -->
  </div>
</template>

<script>
/**
 * options:
 * tooltipSymbol: [default: ''] Symbol for value on tooltip.
 *
 *
 * [Emit]
 * ready: チャートの描画準備ができたタイミング
 * appeared: チャートが描画されたタイミング
 *
 *
 *
 *
 */
import Vue from 'vue';
// import CLoading from '@/components/uikit/CLoading';

// amCharts
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_material from '@amcharts/amcharts4/themes/material';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';

// Fn for chart
import * as chartFn from '@/components/CustomCharts/chartFn';
import { sleeper } from '@/lib/tools';
import moment from 'moment';

let chart;

export default Vue.extend({
  name: 'XYChart',
  // components: { CLoading },
  props: {
    // Data
    data: {
      type: Array,
      required: true,
    },
    category: {
      type: String,
      required: true,
    },
    granularity: {
      type: String,
      required: true,
    },
    conditions: {
      type: Object,
      default: () => {},
    },
    series: {
      type: Array,
      default: () => [],
    },
    totalId: {
      type: String,
    },
    totalLabel: {
      type: String,
    },

    // State
    locale: {
      type: String,
      default: 'en',
    },
    chartType: {
      type: String,
      required: true,
    },

    // Tooltip
    tooltipValuePrefix: {
      type: String,
      default: '',
    },
    tooltipValueSuffix: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  created() {
    am4core.useTheme(am4themes_material);
    // if (this.conditions.animated) {
    am4core.useTheme(am4themes_animated);
    // }
  },
  destroyed() {
    // Dispose all charts
    //am4core.disposeAllCharts();
  },
  watch: {
    locale(val) {
      chartFn.changeLocale(chart, val);
    },
  },
  methods: {
    async generateChart() {
      this.loading = true;
      await sleeper(500);

      // Conditions
      const conditions = this.conditions;

      // Dispose all charts
      //am4core.disposeAllCharts();

      /*
       * Add licence
       */
      am4core.addLicense('CH226353241');

      /*
       * Set initial options
       */
      am4core.options.minPolylineStep = 5;

      /*
       * Generate chart component
       */
      chart = am4core.create('hero-chart-div', am4charts.XYChart);

      chart.data = this.data;
      chart.responsive.enabled = false;
      chart.paddingTop = 64;
      chart.numberFormatter.numberFormat = conditions.yAxisFormat;
      chart.dateFormatter.dateFormat = conditions.xAxisFormat;

      const xAxis = this.setDateAxis(chart, conditions);
      this.setValueAxis(chart, conditions);
      const totalAxis = this.setTotalAxis(chart, conditions, this.series);
      this.setCursor(chart, xAxis);
      this.setLegend(chart);
      this.setExportSetting(chart, conditions);
      this.setEvents(chart);
      this.setSeries(chart, this.series, this.totalId, totalAxis, conditions);
      this.setSettings(chart, conditions);

      // Set locale
      chartFn.changeLocale(chart, this.locale);

      this.loading = false;

      return Promise.resolve();
    },

    setSeries(chart, series, totalId, totalAxis, conditions) {
      let seriesArray = [];

      /*
       * Normal Series
       */
      this.series.forEach(series => {
        // Filtered total series
        if (series.id !== totalId) {
          // Create Series
          const seriesItem = this.creatSeries(series);

          // Push Series
          seriesArray.push(seriesItem);
        }
      });

      /*
       * Total Series
       */
      const totalSeries = this.createTotalSeries(totalAxis);
      seriesArray.unshift(totalSeries);

      /*
       * Tooltip
       */
      seriesArray.forEach(i => {
        this.createTooltip(chart, i, conditions);
        chart.series.push(i);
      });
    },

    exportChart(type) {
      chartFn.exportChart(chart, type);
    },

    createTooltip(chart, series, conditions) {
      const options = {
        valuePrefix: this.tooltipValuePrefix,
        valueSuffix: this.tooltipValueSuffix,
        tooltipNumberFormat: conditions.tooltipNumberFormat,
      };

      series.adapter.add('tooltipHTML', function() {
        let html =
          '<div class="__Chart-XYChart-TooltipText">{date.formatDate(\'yyyy/MM/dd\')}</div>';

        chart.series.each(item => {
          const value = item.dataFields.valueY;

          html += `<div class="__Chart-XYChart-TooltipText-ListItem">
            <div class="__Chart-XYChart-TooltipText-ListItemLabel">
              <span style="color: ${item.stroke.hex}" >●</span> ${item.name}:
            </div>
            <div class="__Chart-XYChart-TooltipText-ListItemValue">
              ${options.valuePrefix}{${value}}${options.valueSuffix}
            </div>
          </div>`;
        });
        return html;
      });

      // Prevent cross-fading of tooltips
      series.tooltip.defaultState.transitionDuration = 1;
      series.tooltip.hiddenState.transitionDuration = 1;
      series.tooltip.getFillFromObject = false;
      series.tooltip.background.fill = am4core.color('#fff');
      series.tooltip.label.fill = am4core.color('#00');
      series.tooltip.numberFormatter.numberFormat = options.tooltipNumberFormat;
      series.tooltip.numberFormatter.smallNumberThreshold = 0.00000000001;
    },

    createTotalSeries(totalAxis) {
      const conditions = this.conditions;

      let series;
      series = new am4charts.LineSeries();

      // Basic
      series.name = this.totalLabel;
      series.id = this.totalId;
      series.zIndex = 1;
      series.yAxis = totalAxis;

      // Data
      series.dataFields.dateX = 'date';
      series.dataFields.valueY = '#total';

      // Style - Stroke
      series.stroke = conditions.totalColor;
      series.strokeWidth = conditions.totalLineWidth;
      series.strokeDasharray =
        conditions.totalStrokeType === 'dashed' || conditions.totalStrokeType === 'dashed-bullet'
          ? '8,4'
          : undefined;
      series.tensionX = 0.9;

      // Style - Bullet
      if (
        conditions.totalStrokeType === 'solid-bullet' ||
        conditions.totalStrokeType === 'dashed-bullet'
      ) {
        const circleBullet = series.bullets.push(new am4charts.CircleBullet());
        circleBullet.circle.fill = conditions.totalColor;
        circleBullet.circle.stroke = am4core.color('#fff');
        circleBullet.circle.strokeWidth = 2;
        series.minBulletDistance = 5;
      }

      // Event
      series.events.on('hidden', function() {
        totalAxis.disabled = true;
      });
      series.events.on('shown', function() {
        totalAxis.disabled = false;
      });

      return series;
    },

    creatSeries(item) {
      let series;

      const conditions = {
        id: item.id,
        name: item.name,
        xAxisLabel: this.conditions.xAxisLabel,
        othersColor: this.conditions.othersColor,
      };

      switch (this.chartType) {
        case 'line':
          series = new am4charts.LineSeries();
          break;
        case 'bar':
          series = new am4charts.ColumnSeries();
          series.columns.template.width = am4core.percent(100);
          break;
        case 'stacked':
          series = new am4charts.ColumnSeries();
          series.stacked = true;
          break;
      }

      // Basic
      series.name = conditions.name;
      series.id = conditions.id;

      // Data Fields - dateX
      series.dataFields.dateX = conditions.xAxisLabel;

      // Data Fields - valueY
      series.dataFields.valueY = conditions.id;

      // Style
      series.connect = false;
      series.tensionX = 0.9;

      // Style - Others
      if (conditions.id === '#others') {
        const color = conditions.othersColor;

        if (color !== 'auto') {
          series.stroke = color;
          series.fill = color;
        }
      }

      return series;
    },

    removeSeries(items) {
      items.forEach(i => {
        const targetIndex = chart.series.values.findIndex(s => s.id === i.id);
        chart.series.removeIndex(targetIndex).dispose();
      });
    },

    /**
     * Date axis
     * @param chart {object}
     * @param conditions {object}
     * @return xAxis
     */
    setDateAxis(chart, conditions) {
      let xAxis = chart.xAxes.push(new am4charts.DateAxis());
      xAxis.groupData = conditions.grouping; // グループ化

      xAxis.renderer.cellStartLocation = 0.2;
      xAxis.renderer.cellEndLocation = 0.8;

      // Date format
      xAxis.periodChangeDateFormats.setKey('month', '[bold]yyyy[/]');

      // Return xAxis
      return xAxis;
    },

    /**
     * Value axis
     * @param chart
     * @param conditions {object}
     */
    setValueAxis(chart, conditions) {
      let yAxis = chart.yAxes.push(new am4charts.ValueAxis());

      yAxis.name = 'valueAxis';
      yAxis.renderer.grid.template.strokeOpacity = 0.2;
      yAxis.layout = 'absolute';
      yAxis.tooltip.disabled = true;

      // Title
      yAxis.title.text = conditions.xAxisTitle;
      yAxis.title.rotation = 0;
      yAxis.title.align = 'center';
      yAxis.title.valign = 'top';
      yAxis.title.dy = -40;
      yAxis.title.fontWeight = '700';

      // Number format
      yAxis.numberFormatter = new am4core.NumberFormatter();
      yAxis.numberFormatter.numberFormat = conditions.yAxisFormat;
      if (conditions.bigNumberPrefixes) {
        yAxis.numberFormatter.bigNumberPrefixes = conditions.bigNumberPrefixes;
      }
      yAxis.numberFormatter.smallNumberThreshold = 0.00000000001;
    },

    /**
     * Total axis
     * @params conditions {object}
     * @return totalAxis
     */
    setTotalAxis(chart, conditions, series) {
      let totalAxis = chart.yAxes.push(new am4charts.ValueAxis());

      totalAxis.name = 'totalAxis';
      totalAxis.layout = 'absolute';
      totalAxis.renderer.opposite = true;

      totalAxis.tooltip.disabled = true;

      // Grid
      if (series.length) {
        totalAxis.renderer.grid.template.disabled = true;
      }

      // Title
      totalAxis.title.text = conditions.totalAxisTitle;
      totalAxis.title.rotation = 0;
      totalAxis.title.align = 'center';
      totalAxis.title.valign = 'top';
      totalAxis.title.dy = -40;
      totalAxis.title.fontWeight = '700';

      // Number format
      totalAxis.numberFormatter = new am4core.NumberFormatter();
      totalAxis.numberFormatter.numberFormat = conditions.yAxisFormat;
      if (conditions.bigNumberPrefixes) {
        totalAxis.numberFormatter.bigNumberPrefixes = conditions.bigNumberPrefixes;
      }
      totalAxis.numberFormatter.smallNumberThreshold = 0.00000000001;

      return totalAxis;
    },

    /**
     * Legend
     * @param chart
     */
    setLegend(chart) {
      // Create legend container
      let legendContainer = am4core.create('hero-chart-legend-div', am4core.Container);
      legendContainer.width = am4core.percent(100);
      legendContainer.height = am4core.percent(100);

      // Move legend to legend container
      chart.legend = new am4charts.Legend();
      chart.legend.parent = legendContainer;

      // Auto resize event
      chart.events.on('datavalidated', resizeLegend);
      chart.events.on('maxsizechanged', resizeLegend);

      chart.legend.events.on('datavalidated', resizeLegend);
      chart.legend.events.on('maxsizechanged', resizeLegend);

      function resizeLegend() {
        document.getElementById('hero-chart-legend-div').style.height =
          chart.legend.contentHeight + 'px';
      }
    },

    /**
     * Cursor - Cursor
     * @param chart
     * @param xAxis
     */
    setCursor(chart, xAxis) {
      chart.cursor = new am4charts.XYCursor();
      chart.cursor.maxTooltipDistance = -1;
      chart.cursor.xAxis = xAxis;
      chart.cursor.fullWidthLineX = true;
      chart.cursor.lineX.strokeWidth = 0;
      chart.cursor.lineX.fill = am4core.color('#000');
      chart.cursor.lineX.fillOpacity = 0.1;
    },

    /**
     * Export setting
     * @param chart
     * @param conditions {object}
     */
    setExportSetting(chart, conditions) {
      const prefix = conditions.exportPrefix;
      const category = this.category;
      const granularity = this.granularity;
      const start = moment(conditions.dateRange.start).format('YYYY-MM-DD');
      const end = moment(conditions.dateRange.end).format('YYYY-MM-DD');

      let filename = `${category}_${granularity}_${start}_${end}`;

      if (prefix !== '') {
        filename = `${prefix}_${filename}`;
      }

      chart.exporting.filePrefix = filename;
    },

    /**
     * Events
     * @param chart
     */
    setEvents(chart) {
      const _this = this;
      chart.events.on('ready', function() {
        _this.$emit('ready');
      });

      chart.events.on('appeared', function() {
        _this.$emit('appeared');
      });

      chart.events.on('datavalidated', function() {
        _this.$emit('datavalidated', chart);
      });

      /**
       * Clickable cursor background.
       * return {object} - date
       */
      chart.plotContainer.events.on('hit', function() {
        let xAxis = chart.xAxes.getIndex(0);
        const date = xAxis.positionToDate(xAxis.toAxisPosition(chart.cursor.xPosition));

        _this.$emit('click-item', {
          date: date,
        });
      });
    },

    /**
     * Settings
     * @param chart
     * @param conditions {object}
     */
    setSettings(chart, conditions) {
      /*
       * Scrollbar - optional
       */
      if (conditions.scrollbar) {
        let scrollbarX = new am4charts.XYChartScrollbar();

        chart.series.each(i => {
          scrollbarX.series.push(i);
        });

        chart.scrollbarX = scrollbarX;
        chart.scrollbarX.parent = chart.bottomAxesContainer;
      }

      /*
       * Setting
       */
      // Display only the series displayed on the chart in the table, not all the data.
      // すべてのデータではなく、チャートに表示されているシリーズのみテーブルに表示させる。
      const _this = this;
      chart.exporting.adapter.add('formatDataFields', function(data) {
        data.dataFields = {};
        chart.series.each(function(series) {
          data.dataFields[series.dataFields.dateX] = series.dataFields.dateX;
          if (series.visible) {
            // Displayed Label
            let label;
            if (series.dataFields.valueY === '#total') {
              label = 'Total';
            } else if (_this.category === 'account') {
              label = `${series.name} [${series.id}]`;
            } else {
              label = series.name;
            }

            data.dataFields[series.dataFields.valueY] = label;
          }
        });

        return data;
      });
    },
  },
});
</script>

<style scoped lang="scss">
.__XYChart {
  position: relative;

  #hero-chart-div {
    height: 560px;
    width: 100%;
  }

  #hero-chart-legend-div {
    height: 100px;
    width: 100%;
  }
}
</style>

<style lang="scss">
.__Chart-XYChart {
  &-TooltipText {
    font-weight: 700;
  }

  &-TooltipText-ListItem {
    display: flex;
    justify-content: space-between;

    &.isBordered {
      border-top: 1px solid $border-color;
      border-bottom: 1px solid $border-color;
      padding: 4px 0;
      margin-bottom: 4px;
    }
  }

  &-TooltipText-ListItemLabel {
    margin-right: $space-sm;
  }

  &-TooltipText-ListItemValue {
  }

  &-TooltipText-ListItemSeparate {
    border-bottom: 1px solid $border-color;
  }
}
</style>
